import React, { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import Tabs from "../../../../../Tabs";
import Preset from "../Preset";
import MediaLibraryReuse from "../../../../../MediaLibrary/MediaLibraryReuse";
import { getSelectedSegment } from "../../../../../../redux/selectors/segmentBuilderSelectors";
import ImageBrowser from "../../../ToolbarMediaBrowser/ImageBrowser";
import ImageCropper from "../../../../../ImageCropper";
import { Stack, CircularProgress } from "@mui/material";
import TextView from "../../../../../TextView";
import CropIcon from "@mui/icons-material/Crop";
import styled from "styled-components";
import UploadMedia from "../../../UploadMedia";
import UploadedImage from "../../../../Main/PortraitTab/UploadedImage";
import EmbedURL from "../../../EmbedURL";
import RecentMedia from "../../../RecentMedia";
import FeatureEligibility from "../../../../../FeatureEligibility";
import { FEATURES } from "../../../../../../constants/features";
import { useIfUserEligible } from "../../../../../../hooks/useIfUserEligible";
import { ANIMATED_LOOPS_ENABLED } from "../../../../../animatedLoops/flag";
import AnimatedLoopLibrary from "../../../../../animatedLoops/AnimatedLoopLibrary";
import { isGradientWallpaper } from "../../wallpaperValue";
import {
  orderSourceTabs,
  SOURCE_STRIP_SX,
} from "../../../MediaSelection/sourceTabOrder";

export const CropStack = styled(Stack)`
  background-color: rgba(156, 39, 176, 0.1);
  border-radius: 2px;
  padding: 1rem;
  margin-top: 2rem;
  cursor: pointer;
  cursor: ${(props) => (props.enable ? "pointer" : "not-allowed")};

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  opacity: ${(props) => props.opacity || 0.5};
`;

function ImageView({
  handleSelectWallpaper,
  bgWallpaper,
  isPattern,
  currentBg,
  handleSelectBackgroundResizeMode,
  resizeMode,
  handleEditImage,
  handleRemoveImage,
  showPreview = true,
  showEmbedURLDisclaimer = false,
  // O1 (Fable UX pass): the live wheel/segment colour, threaded from the Wallpaper
  // host, so the loop "Segment" auto-match pill actually renders.
  segmentColor = undefined,
}) {
  // O2 (Fable UX pass): one name for the loop library everywhere. The shared media
  // picker already calls this source "Loops"; the wallpaper picker said "Animated"
  // (reads as a setting, not a source). Unified to "Loops".
  // T22.146: same strip, same order, same labels as every other media picker
  // (Upload · Embed URL · Browse · Preset · Recent); Loops follows. Was
  // Preset · Loops · Upload · URL · Browse · Recent, with Browse/Recent clipped.
  const categories = useMemo(
    () =>
      orderSourceTabs([
        "Upload",
        "Embed URL",
        "Browse",
        "Preset",
        "Recent",
        ...(ANIMATED_LOOPS_ENABLED ? ["Loops"] : []),
      ]),
    []
  );
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [cropperVisible, setCropperVisible] = useState(false);

  const {
    isEligible,
    subscription,
    isCheckingEligibility,
    requiredSubscription,
  } = useIfUserEligible(FEATURES.WHEEL_WALLPAPER_IMAGE);

  const handleCategoryChange = useCallback((event, newValue) => {
    setSelectedCategory(newValue);
  }, []);

  const options = [
    { label: "Contain", value: 1 },
    { label: "Cover", value: 2 },
  ];

  const [selectedOption, setSelectedOption] = useState(resizeMode);
  const [open, setOpen] = useState(false);

  // DL-048 adoption: this segment-wallpaper image picker accepts images only
  // (image/gif — never audio/video/document); name the open Segment on the modal chip.
  const selectedSegment = useSelector(getSelectedSegment);
  const segmentName = selectedSegment?.settings?.title || null;

  const handleRadioChange = (event) => {
    setSelectedOption(event.target.value);
    handleSelectBackgroundResizeMode(event.target.value);
  };

  if (isCheckingEligibility) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "20px" }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (!isEligible) {
    return (
      <FeatureEligibility
        subscription={subscription}
        requiredSubscription={requiredSubscription}
      />
    );
  }

  return (
    <div>
      {showPreview
        ? bgWallpaper &&
          !isGradientWallpaper(bgWallpaper) &&
          ["Upload", "Browse", "Embed URL", "Preset", "Recent", "Loops"].includes(
            selectedCategory
          ) && (
            <UploadedImage
              handleRemoveImage={handleRemoveImage}
              image={bgWallpaper?.src || bgWallpaper}
              setOpen={setOpen}
            />
          )
        : null}

      <Tabs
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        color={"#81459B"}
        indicatorColor="#C283DA"
        sx={SOURCE_STRIP_SX}
      />

      {/* T22.139: Reuse lives on the Upload tab with the drop zone. */}
      {selectedCategory === "Upload" && (
        <MediaLibraryReuse
          acceptedTypes={["image"]}
          segmentName={segmentName}
          contextLabel="a wallpaper"
          onInsert={(value) => handleSelectWallpaper(value?.src)}
        />
      )}

      {selectedCategory === "Preset" ? (
        <Preset
          currentBg={currentBg}
          handleSelectWallpaper={handleSelectWallpaper}
          bgWallpaper={bgWallpaper}
        />
      ) : ANIMATED_LOOPS_ENABLED && selectedCategory === "Loops" ? (
        <AnimatedLoopLibrary
          segmentColor={segmentColor}
          onSelect={(dataUri) => handleSelectWallpaper(dataUri)}
        />
      ) : selectedCategory === "Upload" ? (
        <UploadMedia
          type="image"
          // ISP-9999: the Wheel/Segment Wallpaper is a STATIC surface (Color /
          // Gradient / Image / Pattern) — no animated GIF. (Animated GIF fills
          // live on the Segment FILL picker, not the Wallpaper.)
          allowGif={false}
          handleMedia={(val) => handleSelectWallpaper(val?.src)}
        />
      ) : selectedCategory === "Browse" ? (
        <ImageBrowser
          onConfirm={(image) => handleSelectWallpaper(image?.src)}
        />
      ) : selectedCategory === "Embed URL" ? (
        <EmbedURL
          defaultValue={""}
          type={"image"}
          onConfirm={(value) => handleEditImage(value?.src)}
          showDisclaimer={showEmbedURLDisclaimer}
        />
      ) : selectedCategory == "Recent" ? (
        <RecentMedia
          type={"image"}
          onClickMedia={(value) => handleSelectWallpaper(value.src)}
        />
      ) : null}
    </div>
  );
}

export default ImageView;
